/**
 * Copyright (c), Vector Engineering LLC
 * All rights reserved.
 * 
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 * Commercial use requires explicit written permission.
 */

import axios from 'axios';
import { GenerationStep } from '../components/GenerationProgress';
import apiService from './api';

// Generation status polling interval in milliseconds
const POLLING_INTERVAL = 2000; // 2 seconds

// Status type from backend
interface GenerationStatus {
  status: string;
  progress: number;
  currentStep: string;
  details?: Record<string, any>;
  error?: string;
  updatedAt: string;
  scriptId?: string;
}

// Initialize generation tracking
let activePolling: { [key: string]: NodeJS.Timeout } = {};

/**
 * Maps backend step strings to frontend GenerationStep type
 * @param step Backend step string
 * @returns Frontend GenerationStep
 */
const mapStepToGenerationStep = (step: string): GenerationStep => {
  // Handle scene generation steps (scene-1, scene-2, etc.)
  if (step.startsWith('scene-')) {
    // Check for scene completion
    if (step.endsWith('-complete')) {
      // Extract scene number and return the next scene step (if we're on scene-1-complete, return scene-2)
      const sceneNum = parseInt(step.split('-')[1], 10);
      return `scene-${sceneNum + 1}` as GenerationStep;
    }
    // Return the current scene step
    return step as GenerationStep;
  }

  // Map other steps
  switch (step) {
    case 'preparing':
      return 'preparing';
    case 'outline':
    case 'outline_complete':
    case 'parsing_outline':
    case 'scenes_identified':
    case 'scenes_ready':
      return 'outline';
    case 'combining':
    case 'assembled':
      return 'combining';
    case 'saving':
      return 'saving';
    case 'complete':
    case 'complete_fallback':
      return 'complete';
    case 'error':
    default:
      return 'error';
  }
};

/**
 * Start generation and begin polling for status updates
 * @param prompt Generation prompt data
 * @param onProgress Callback function for progress updates
 * @returns Promise that resolves when complete, with the script ID
 */
const startGeneration = async (
  prompt: any,
  onProgress: (step: GenerationStep, progress: number, details?: any) => void
): Promise<string> => {
  try {
    // Log generation attempt
    console.info('Starting script generation:', {
      title: prompt.title,
      theme: prompt.theme,
      setting: prompt.setting,
      wordCount: prompt.target_word_count,
      duration: prompt.duration,
      timestamp: Date.now()
    });

    // Start initial status as preparing
    onProgress('preparing', 0);

    // Use the configured API instance from api.ts
    const response = await apiService.createScript(prompt);

    // Get script ID from response
    if (!response || !response.id) {
      throw new Error('No script ID returned from generation');
    }

    const scriptId = response.id;

    // For the standard API, we already have the complete script
    // Just simulate progress steps for UI feedback
    onProgress('outline', 25);

    setTimeout(() => {
      onProgress('scene-1', 50);

      setTimeout(() => {
        onProgress('combining', 75);

        setTimeout(() => {
          onProgress('complete', 100, { scriptId });
        }, 500);
      }, 500);
    }, 500);

    // Return the script ID
    return scriptId;
  } catch (error: unknown) {
    // Log detailed error
    console.error('Script generation failed:', {
      error: error instanceof Error ? error.message : 'Unknown error',
      prompt: {
        title: prompt.title,
        theme: prompt.theme,
        setting: prompt.setting
      },
      timestamp: Date.now()
    });

    // Show appropriate error in UI
    onProgress('error', 0, { 
      error: error instanceof Error ? error.message : 'Unknown error occurred during script generation'
    });

    // Re-throw with context
    throw new Error(`Failed to generate script: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Stop polling for a specific generation
 * @param generationId Generation ID to stop polling for
 */
const stopPolling = (generationId: string) => {
  if (activePolling[generationId]) {
    clearInterval(activePolling[generationId]);
    delete activePolling[generationId];
  }
};

/**
 * Stop all active polling intervals
 */
const stopAllPolling = () => {
  Object.keys(activePolling).forEach(id => {
    clearInterval(activePolling[id]);
  });
  activePolling = {};
};

// Export the service
const generationService = {
  startGeneration,
  stopPolling,
  stopAllPolling
};

export default generationService;
